import { logger } from "@/lib/logger";

import type { AuthAdapter, AuthIdentity } from "../types";

/**
 * Fixed local identity for development.
 *
 * Reaching this adapter requires `auth.mode: "dev-stub"`, `auth.allowDevStub:
 * true`, and `environment: "local"` simultaneously. Any weaker combination is
 * rejected by the runtime-configuration schema before the adapter is
 * constructed, so a deployed environment cannot fall back to a stub identity by
 * omission.
 *
 * The token it returns is not a credential. It is a marker the local Editor
 * Service recognises, and it carries no signature and no claims.
 */
const DEV_STUB_TOKEN = "dev-stub";

const DEV_STUB_IDENTITY: AuthIdentity = {
  subject: "dev-stub-user",
  displayName: "Local developer",
  email: "developer@localhost",
};

export function createDevStubAdapter(): AuthAdapter {
  let signedIn = true;

  return {
    kind: "dev-stub",

    // There is no session to end. The boot sequence starts an interactive
    // sign-in the moment no identity is present, and this adapter always
    // grants one, so signing out and being signed straight back in is the only
    // possible outcome. Callers disable the control and say why rather than
    // offering one that cannot work.
    supportsSignOut: false,

    // The stub identity is fixed; there is no second account to switch to.
    supportsAccountSwitch: false,

    async initialize() {
      logger.warn("Using the development stub identity", {
        environment: "local",
        reason: "auth.mode=dev-stub",
      });
    },

    async getAccessToken() {
      return signedIn ? DEV_STUB_TOKEN : null;
    },

    getIdentity() {
      return signedIn ? DEV_STUB_IDENTITY : null;
    },

    async signIn() {
      signedIn = true;
    },

    async switchAccount() {
      // Unsupported by design; the UI disables the control with a reason.
      logger.warn("The development stub identity cannot switch accounts");
    },

    async clearLocalSession() {
      signedIn = false;
    },

    async signOut() {
      signedIn = false;
    },
  };
}
